import './FullscreenPhotoViewer.css';

import React, {
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';

export interface FullscreenPhotoViewerProps {
  open: boolean;
  /** Image URL (e.g. data URL or https). */
  src: string;
  onClose: () => void;
  /** Passed to `<img alt>`; use a real description when the image is meaningful. */
  alt?: string;
  /** `aria-label` on the dialog when there is no visible title. */
  viewerLabel?: string;
}

const FullscreenPhotoViewer: React.FC<FullscreenPhotoViewerProps> = ({
  open,
  src,
  onClose,
  alt = '',
  viewerLabel = 'Photo preview',
}) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousActiveElementRef = useRef<HTMLElement | null>(null);

  const onCloseStable = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseStable();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onCloseStable]);

  useEffect(() => {
    if (!open) {
      return;
    }
    previousActiveElementRef.current = document.activeElement as HTMLElement | null;
    const id = window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });
    return () => {
      window.cancelAnimationFrame(id);
      previousActiveElementRef.current?.focus?.();
    };
  }, [open]);

  if (!open || !src.trim()) {
    return null;
  }

  const overlay = (
    <div
      className="fullscreen-photo-viewer-overlay"
      role="presentation"
      onClick={onCloseStable}
    >
      <button
        ref={closeButtonRef}
        type="button"
        className="fullscreen-photo-viewer-close"
        aria-label="Close"
        onClick={(e) => {
          e.stopPropagation();
          onCloseStable();
        }}
      >
        <span className="fullscreen-photo-viewer-close-glyph" aria-hidden>
          ×
        </span>
      </button>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={viewerLabel}
        className="fullscreen-photo-viewer-dialog"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <img src={src} alt={alt} draggable={false} />
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
};

export default FullscreenPhotoViewer;
